
import { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
const API_BASE = import.meta.env.VITE_API_URL;

function obtenerCSRF() {
  const cookie = document.cookie
    .split("; ")
    .find((item) => item.startsWith("turnomatico_csrftoken="));

  return cookie
    ? decodeURIComponent(cookie.split("=").slice(1).join("="))
    : "";
}

export default function EncuestaSatisfaccion() {
  const { token } = useParams();

  const [estado, setEstado] = useState("cargando");
  const [error, setError] = useState("");
  const [enviando, setEnviando] = useState(false);

  const [respuestas, setRespuestas] = useState({
    claridad: null,
    resolucion: "",
    evaluacion_general: "",
    comentario: "",
  });

  useEffect(() => {
    let activo = true;

    async function cargarEncuesta() {
      try {
        const respuesta = await fetch(`${API_BASE}/feedback/encuesta/${token}/`, {
          credentials: "include",
        });
        const datos = await respuesta.json();

        if (!activo) return;

        if (!respuesta.ok) {
          setError(datos.error || "No fue posible abrir la encuesta.");
          setEstado("error");
          return;
        }

        setEstado("disponible");
      } catch {
        if (activo) {
          setError("No fue posible conectar con el servidor.");
          setEstado("error");
        }
      }
    }

    cargarEncuesta();

    return () => {
      activo = false;
    };
  }, [token]);

  function actualizar(campo, valor) {
    setRespuestas((anterior) => ({
      ...anterior,
      [campo]: valor,
    }));
  }

  async function enviarEncuesta(evento) {
    evento.preventDefault();

    if (enviando) return;

    setEnviando(true);
    setError("");

    try {
      const respuesta = await fetch(
        `${API_BASE}/feedback/encuesta/${token}/responder/`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            "X-CSRFToken": obtenerCSRF(),
          },
          credentials: "include",
          body: JSON.stringify(respuestas),
        }
      );

      const datos = await respuesta.json();

      if (!respuesta.ok) {
        setError(datos.error || "No fue posible guardar tus respuestas.");
        return;
      }

      setEstado("completada");
    } catch {
      setError("Ocurrió un problema de conexión. Inténtalo nuevamente.");
    } finally {
      setEnviando(false);
    }
  }

  return (
    <main className="container py-5" style={{ maxWidth: 680 }}>
      <div className="card border-0 shadow-sm">
        <div className="card-body p-4 p-md-5">
          <h1 className="h3 mb-2">Encuesta de satisfacción</h1>
          <p className="text-muted mb-4">
            Oficina de Administración Docente
          </p>

          {estado === "cargando" && (
            <p>Estamos cargando tu encuesta...</p>
          )}

          {estado === "error" && (
            <div className="alert alert-warning" role="alert">
              {error}
            </div>
          )}

          {estado === "completada" && (
            <div className="alert alert-success" role="status">
              ¡Muchas gracias por tu respuesta! Tu opinión nos ayuda
              a mejorar nuestra atención.
            </div>
          )}

          {estado === "disponible" && (
            <form onSubmit={enviarEncuesta}>
              <fieldset className="mb-4">
                <legend className="fs-6 fw-semibold">
                  1. ¿Qué tan clara fue la información entregada durante
                  tu atención?
                </legend>
                <div className="d-flex flex-wrap gap-2 mt-3">
                  {[1, 2, 3, 4, 5].map((numero) => (
                    <label
                      className={`btn ${
                        respuestas.claridad === numero
                          ? "btn-primary"
                          : "btn-outline-primary"
                      }`}
                      key={numero}
                    >
                      <input
                        className="visually-hidden"
                        type="radio"
                        name="claridad"
                        value={numero}
                        checked={respuestas.claridad === numero}
                        onChange={() => actualizar("claridad", numero)}
                        required
                      />
                      {numero}
                    </label>
                  ))}
                </div>
                <div className="d-flex justify-content-between mt-2">
                  <small className="text-muted">Nada clara</small>
                  <small className="text-muted">Muy clara</small>
                </div>
              </fieldset>

              <fieldset className="mb-4">
                <legend className="fs-6 fw-semibold">
                  2. ¿Tu consulta o solicitud fue resuelta durante la
                  atención?
                </legend>
                {[
                  ["completa", "Sí, completamente"],
                  ["parcial", "Parcialmente"],
                  ["pendiente", "No, quedó pendiente"],
                ].map(([valor, texto]) => (
                  <div className="form-check mt-2" key={valor}>
                    <input
                      className="form-check-input"
                      type="radio"
                      name="resolucion"
                      id={`resolucion-${valor}`}
                      value={valor}
                      checked={respuestas.resolucion === valor}
                      onChange={() => actualizar("resolucion", valor)}
                      required
                    />
                    <label
                      className="form-check-label"
                      htmlFor={`resolucion-${valor}`}
                    >
                      {texto}
                    </label>
                  </div>
                ))}
              </fieldset>

              <fieldset className="mb-4">
                <legend className="fs-6 fw-semibold">
                  3. ¿Cómo evaluarías tu experiencia de atención en la
                  Oficina de Administración Docente?
                </legend>
                {[
                  ["excelente", "Excelente"],
                  ["buena", "Buena"],
                  ["regular", "Regular"],
                  ["mala", "Mala"],
                  ["muy_mala", "Muy mala"],
                ].map(([valor, texto]) => (
                  <div className="form-check mt-2" key={valor}>
                    <input
                      className="form-check-input"
                      type="radio"
                      name="evaluacion_general"
                      id={`evaluacion-${valor}`}
                      value={valor}
                      checked={respuestas.evaluacion_general === valor}
                      onChange={() => actualizar("evaluacion_general", valor)}
                      required
                    />
                    <label
                      className="form-check-label"
                      htmlFor={`evaluacion-${valor}`}
                    >
                      {texto}
                    </label>
                  </div>
                ))}
              </fieldset>

              <div className="mb-4">
                <label className="form-label fw-semibold" htmlFor="comentario">
                  4. ¿Tienes algún comentario o sugerencia para mejorar
                  nuestra atención?
                </label>
                <textarea
                  className="form-control"
                  id="comentario"
                  rows={3}
                  maxLength={2000}
                  placeholder="Opcional"
                  value={respuestas.comentario}
                  onChange={(evento) =>
                    actualizar("comentario", evento.target.value)
                  }
                />
              </div>

              {error && (
                <div className="alert alert-danger" role="alert">
                  {error}
                </div>
              )}

              <button
                type="submit"
                className="btn btn-primary w-100"
                disabled={enviando}
              >
                {enviando ? "Enviando..." : "Enviar respuestas"}
              </button>
            </form>
          )}
        </div>
      </div>
    </main>
  );
}

